import Draggable from 'react-draggable';
import { useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSort } from '../../../hooks/useSort';
import {
  AppKeys,
  MenuApplications,
  NavigationPad,
  NumberPad,
  Playback,
  PowerOther,
  TVControls,
} from './CustomButtons';
import { useCitrusView } from '../../../contexts/CitrusViewContext';
import clsx from 'clsx';

interface Props {
  fullscreen: boolean;
  hidden: boolean;
  onHide(): void;
}

export const Remote = ({ fullscreen, hidden, onHide }: Props) => {
  const { connected, isMobile, remote, remoteLoading, sendCommand, source } =
    useCitrusView();
  const { sortAsc } = useSort<{ order: number }>('order');
  const ref = useRef<HTMLDivElement | null>(null);
  const buttons = remote?.buttons ?? [];
  const remoteEle = document.getElementById('monitor-remote');
  const bounds = {
    left: 0,
    right: window.screen.availWidth - (remoteEle ? remoteEle?.offsetWidth : 0),
    top: 0,
    bottom: 95,
  };

  const getButtonsByCategory = (category: ButtonCategory) => {
    return buttons
      .filter((button) => button.category === category)
      .sort(sortAsc);
  };

  if (
    buttons.length === 0 ||
    connected === false ||
    hidden ||
    source === undefined ||
    isMobile
  )
    return null;

  return createPortal(
    <Draggable
      defaultClassName="z-[9990] shadow-2xl"
      bounds={bounds}
      defaultPosition={{
        x: window.innerWidth - 240,
        y: 70,
      }}
    >
      <div
        id="monitor-remote"
        className={clsx(
          'absolute grid grid-cols-3 w-[220px] bg-[#111111] top-0 px-6 pt-6 pb-10 rounded-3xl shadow-[inset_0_-4px_2px_rgba(0,0,0,0.2)] after:w-[95%] after:h-[98%] after:absolute after:top-2 after:left-[6px] after:rounded-3xl after:shadow-[inset_0_0_10px_rgba(255,255,255,0.3)] after:border after:border-white/70 after:mix-blend-soft-light after:pointer-events-none',
          { 'gap-2': window.devicePixelRatio <= 1 }
        )}
        ref={ref}
      >
        {remoteLoading ? (
          <div className="absolute -top-1 left-[calc(50%-8px)] z-30 bg-red-500 w-4 h-4 rounded-full animate-ping mix-blend-soft-light" />
        ) : null}
        <div className="relative after:absolute after:z-10 after:w-6 after:h-4 after:rounded-b-full after:bg-black after:left-[calc(50%-12px)] after:-top-6 before:z-2 before:absolute before:z-20 before:left-[calc(50%-8px)] before:-top-7 before:rounded-full before:h-4 before:w-4 before:bg-red-600 col-span-full w-full before:shadow-[inset_0_-3px_2px_rgba(0,0,0,0.4)] m-auto"></div>
        <PowerOther
          buttons={getButtonsByCategory('OTHER')}
          onClick={sendCommand}
          onHide={onHide}
        />
        <Playback
          buttons={getButtonsByCategory('PLAYBACK')}
          onClick={sendCommand}
        />
        <MenuApplications
          buttons={getButtonsByCategory('MENU APPLICATIONS')}
          onClick={sendCommand}
        />
        <NavigationPad
          buttons={getButtonsByCategory('NAVIGATION')}
          onClick={sendCommand}
        />
        <AppKeys
          buttons={getButtonsByCategory('APPKEYS')}
          onClick={sendCommand}
        />
        <TVControls
          buttons={getButtonsByCategory('TV CONTROLS')}
          onClick={sendCommand}
        />
        <NumberPad
          buttons={getButtonsByCategory('NUMPAD')}
          onClick={sendCommand}
        />
        <p className="col-span-full text-center mt-4">Tangerine Remote</p>
      </div>
    </Draggable>,
    document.getElementById(fullscreen ? 'remote-monitor-wrapper' : 'root') ??
      document.body
  );
};

type ButtonCategory =
  | 'OTHER'
  | 'APPKEYS'
  | 'PLAYBACK'
  | 'NUMPAD'
  | 'TV CONTROLS'
  | 'NAVIGATION'
  | 'MENU APPLICATIONS';

export const baseButtonClass = clsx(
  'disabled:pointer-events-none disabled:opacity-30 active:ring-1 hover:ring-1 hover:ring-offset-2 hover:ring-offset-black active:ring-primary hover:ring-primary border-1 border-border-black ring-2 ring-black/80 relative shadow-[inset_0_2px_4px_rgba(255,255,255,0.7)] active:shadow-none active:bg-[#FFD698] transition-all duration-100 active:translate-y-[1px] p-1 bg-gray-300 text-black font-bold rounded text-xs before:active:w-full before:active:h-full before:active:bg-[#FFD698] before:active:blur-sm before:active:mix-blend-overlay',
  {
    'scale-70': window.devicePixelRatio > 1,
    'active:scale-95': window.devicePixelRatio <= 1,
  }
);
